/**
 * Maps assistant-ui message IDs to their artifact names by reading
 * from the AG-UI agent state.
 *
 * The backend patches /artifacts/{backendMessageId} via StateDeltaEvent
 * at the end of each run. This hook bridges the gap between backend
 * message IDs (final:uuid) and assistant-ui message IDs (random UUIDs)
 * by looking up the current assistant message when state changes arrive.
 */
import { useEffect, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { AssistantRuntime } from "@assistant-ui/react";

/**
 * Returns a map of assistant-ui message ID → artifact names for all
 * streaming turns in the current session. Each MessageArtifacts component
 * looks up its own entry by message.id.
 */
export function useStreamingArtifacts(
  agent: HttpAgent,
  runtime: AssistantRuntime
): Map<string, string[]> {
  const [artifactMap, setArtifactMap] = useState<Map<string, string[]>>(() => new Map());

  useEffect(() => {
    setArtifactMap(new Map());

    // Artifact keys that existed before the current run started.
    // Populated from agent.state on each run start so that prior-turn
    // artifacts (loaded via /connect) are never mis-attributed to the
    // current streaming message.
    let baselineArtifactKeys = new Set<string>();
    let isRunning = false;

    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        isRunning = true;
        const currentArtifacts = (agent.state as Record<string, unknown>)?.artifacts;
        baselineArtifactKeys = new Set(
          currentArtifacts && typeof currentArtifacts === "object"
            ? Object.keys(currentArtifacts)
            : []
        );
      },
      onRunFinishedEvent: () => {
        isRunning = false;
      },
      onRunFailed: () => {
        isRunning = false;
      },
      onStateChanged: ({ state }) => {
        if (!isRunning) return;

        const artifacts = state?.artifacts;
        if (!artifacts || typeof artifacts !== "object") return;

        const newKeys = Object.keys(artifacts).filter((k) => !baselineArtifactKeys.has(k));
        if (newKeys.length === 0) return;

        for (const k of newKeys) baselineArtifactKeys.add(k);

        // Look up the current assistant message lazily — by the time a
        // StateDeltaEvent with artifacts arrives, the streaming message
        // is guaranteed to exist in the thread.
        const messages = runtime.thread.getState().messages;
        let assistantId: string | null = null;
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i]!.role === "assistant") {
            assistantId = messages[i]!.id;
            break;
          }
        }
        if (!assistantId) return;

        const latestNewKey = newKeys[newKeys.length - 1]!;
        const names = artifacts[latestNewKey];
        if (!Array.isArray(names)) return;

        setArtifactMap((prev) => {
          const next = new Map(prev);
          next.set(assistantId, names);
          return next;
        });
      },
    });

    return () => sub.unsubscribe();
  }, [agent, runtime]);

  return artifactMap;
}
